import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  TextField,
  Chip,
  FormControl,
  FormHelperText,
  Tooltip,
  Popper,
  Paper,
  Typography,
  InputAdornment,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import SearchIcon from '@mui/icons-material/Search';
import { toTitleCase } from '../../utils/casing';

// EXACT same width map as DropdownComponent
const sizeOverrides = {
  'extra-small': '219px',
  small: '301px',
  medium: '403px',
  large: '534px',
  half: '500px',
  full: '1000px',
  'vehicle-return': '324px',
  'vehicle-return-full': '662px',
  'vehicle-full': '448px',
  'accounts-half': '416px',
  'accounts-full': '864px',
};

/**
 * SearchTagSelectComponent
 * - Searchable multi-select that renders chosen items as chips ABOVE the input
 * - Styling matches DropdownComponent (padding, radii, fonts, borders, helper text)
 */
const SearchTagSelectComponent = ({
  name,
  question,
  value,
  onChange,
  onNext,
  setValidationState,
  registerValidation,
  isMobile,
  layoutVariant = 'default',
}) => {
  const isAccountLayout = layoutVariant === 'account';
  const [error, setError] = useState('');
  const [revealError, setRevealError] = useState(false); // controls when to show visual error
  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const fieldRef = useRef(null);

  // ---------- sizing ----------
  const fieldWidth = question?.customSize
    ? sizeOverrides[question.customSize] || '100%'
    : '100%';
  const responsiveWidth = isAccountLayout
    ? '100%'
    : isMobile
      ? '80vw'
      : fieldWidth;
  const fieldHeight = isMobile ? '51px' : '53px';
  const fontSize = '1rem';

  const borderOverrides = question?.tooltip?.message
    ? {
        '&:hover fieldset': { borderColor: '#E3E800' },
        '&.Mui-focused fieldset': { borderColor: '#E3E800' },
      }
    : {};

  // ---------- options ----------
  const options = useMemo(() => {
    const raw = question?.options || [];
    if (!Array.isArray(raw)) return [];
    return raw.map((opt) =>
      typeof opt === 'string'
        ? { label: opt, value: opt }
        : {
            label: opt.label ?? String(opt.value ?? ''),
            value: opt.value ?? opt.label,
          }
    );
  }, [question]);

  const selected = useMemo(() => {
    const arr = Array.isArray(value) ? value : [];
    return arr.map((v) => (typeof v === 'string' ? { label: v, value: v } : v));
  }, [value]);

  // ---------- validation (silent vs revealed) ----------
  const validateWith = (arr, { reveal = false } = {}) => {
    const ok = !question?.validation?.required || arr.length > 0;

    // Always tell the parent current validity
    setValidationState?.(name, ok);

    // Only show visual error when revealing is requested or already enabled
    if (reveal || revealError) {
      setError(
        ok ? '' : question?.validation?.message || 'Please select at least one'
      );
    } else {
      setError('');
    }
    return ok;
  };

  //const validate = () => validateWith(selected);

  // Register a validator that REVEALS errors (used by the parent "Next" button)
  useEffect(() => {
    registerValidation?.(name, () => {
      setRevealError(true);
      return validateWith(selected, { reveal: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, selected, question?.validation?.required]);

  // Keep parent validity up-to-date on external value changes, silently
  useEffect(() => {
    validateWith(selected, { reveal: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const setParent = (vals) =>
    onChange?.(
      name,
      vals.map((v) => v.value)
    );

  const removeChip = (val) => {
    const next = selected.filter((s) => s.value !== val);
    setParent(next);
    setRevealError(true);
    validateWith(next, { reveal: true });
  };

  const exactMatch = (label) =>
    options.find((o) => o.label.toLowerCase() === label.trim().toLowerCase());

  const addFromInput = (label) => {
    const match = exactMatch(label);
    if (!match) return; // only allow from list
    const next = [...selected, match].filter(
      (v, i, arr) => arr.findIndex((x) => x.value === v.value) === i
    );
    setParent(next);
    setInputValue('');
    setRevealError(true);
    if (validateWith(next, { reveal: true })) onNext?.();
  };

  // ---------- popper/paper ----------
  const StyledPopper = (props) => (
    <Popper {...props} placement="bottom-start" />
  );
  const StyledPaper = (props) => (
    <Paper
      {...props}
      sx={{
        mt: '10px',
        width: isAccountLayout ? '100%' : isMobile ? '80vw' : fieldWidth,
        maxHeight: `calc(${fieldHeight} * 6)`,
        overflowY: 'auto',
        border: '1px solid #A2ACB1',
        borderRadius: '12px',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.06)',
        '&::-webkit-scrollbar': { width: 0, height: 0 },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        zIndex: 1301,
        '& .MuiAutocomplete-listbox': {
          maxHeight: `calc(${fieldHeight} * 6)`,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 0, height: 0 },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        },
      }}
    />
  );

  // ---------- Autocomplete ----------
  const inputControl = (
    <Autocomplete
      multiple
      options={options}
      value={selected}
      inputValue={inputValue}
      onInputChange={(_e, v) => setInputValue(v)}
      onChange={(_e, _newVals, reason, details) => {
        if (reason === 'selectOption' && details?.option) {
          const next = [...selected, details.option].filter(
            (v, i, arr) => arr.findIndex((x) => x.value === v.value) === i
          );
          setParent(next);
          setInputValue('');
          setRevealError(true);
          if (validateWith(next, { reveal: true })) onNext?.();
        }
      }}
      filterSelectedOptions
      disablePortal={false}
      disableClearable
      forcePopupIcon={false}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      getOptionLabel={(o) => toTitleCase(o.label)}
      PopperComponent={StyledPopper}
      PaperComponent={StyledPaper}
      ListboxProps={{
        sx: {
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: 'transparent !important',
          },
          '& .MuiAutocomplete-option.Mui-focused[aria-selected="true"]': {
            backgroundColor: 'transparent !important',
          },
        },
      }}
      renderOption={(props, option) => (
        <li {...props} style={{ listStyle: 'none' }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize,
              color: '#162F3B',
              height: fieldHeight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              px: '28px',
              width: '100%',
            }}
          >
            {toTitleCase(option.label)}
          </Typography>
        </li>
      )}
      renderTags={() => null}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={fieldRef}
          label={inputValue ? toTitleCase(question.label) : ''}
          placeholder={inputValue ? '' : question.placeholder || ''}
          error={!!(revealError && error)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setRevealError(true);
            validateWith(selected, { reveal: true });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && inputValue.trim()) {
              e.preventDefault();
              addFromInput(inputValue);
            }
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon sx={{ fontSize: 22 }} />
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            shrink: !!inputValue,
            sx: { color: '#738289' },
          }}
          sx={{
            width: isAccountLayout ? '100%' : isMobile ? '80vw' : fieldWidth,
            mx: isAccountLayout ? 'auto' : isMobile ? 'auto' : undefined,
            maxWidth: '100%',
            '& .MuiOutlinedInput-root': {
              height: fieldHeight,
              borderRadius: '12px',
              padding: '0 12px',
              gap: '8px',
              borderWidth: '1px',
              transition: 'none',
              '& fieldset': {
                borderRadius: '12px',
                borderWidth: '1px',
                borderColor:
                  revealError && error
                    ? '#D32F2F'
                    : focused
                      ? '#E3E800'
                      : '#A2ACB1',
              },
              ...borderOverrides,
              '& .MuiAutocomplete-endAdornment': {
                right: 8,
                '& .MuiAutocomplete-clearIndicator': { display: 'none' },
                '& .MuiAutocomplete-popupIndicator': { display: 'none' },
              },
              '& input': {
                fontFamily: 'Inter, sans-serif',
                fontSize,
                lineHeight: '1.2rem',
                letterSpacing: '-0.02em',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                textAlign: 'left',
                color: inputValue ? '#162F3B' : '#738289',
                '&::placeholder': {
                  color: '#738289',
                  opacity: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              },
            },
            '& .MuiFormHelperText-root': {
              textAlign: 'right',
              fontFamily: 'Inter, sans-serif',
              fontSize: isMobile ? '0.6rem' : '0.75rem',
              color: '#D32F2F',
              mt: '4px',
              pr: '8px',
            },
          }}
        />
      )}
      componentsProps={{
        paper: { elevation: 0 },
        popper: {
          modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
        },
      }}
      sx={{
        '& .MuiAutocomplete-option[aria-selected="true"]': {
          backgroundColor: 'transparent !important',
        },
        '& .MuiAutocomplete-option.Mui-focused': {
          backgroundColor: 'transparent !important',
        },
        '& .MuiAutocomplete-option.Mui-focused[aria-selected="true"]': {
          backgroundColor: 'transparent !important',
        },
      }}
    />
  );

  const content = (
    <Box sx={{ width: '100%' }}>
      {/* Chips above the input (deletable) */}
      {selected.length > 0 && (
        <Box
          sx={{
            width: responsiveWidth,
            mx: isAccountLayout ? 'auto' : isMobile ? 'auto' : undefined,
            maxWidth: '100%',
            mb: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
          }}
        >
          {selected.map((opt) => (
            <Chip
              key={opt.value}
              label={toTitleCase(opt.label)}
              onDelete={() => removeChip(opt.value)}
              size="small"
              sx={{
                borderRadius: '8px',
                backgroundColor: '#A2ACB1',
                color: '#162F3B',
              }}
            />
          ))}
        </Box>
      )}

      {inputControl}

      {/* Render helper text only after we decide to reveal */}
      {revealError && error && (
        <FormHelperText
          sx={{
            textAlign: 'right',
            fontFamily: 'Inter, sans-serif',
            fontSize: isMobile ? '0.6rem' : '0.75rem',
            color: '#D32F2F',
            mt: '4px',
            pr: '8px',
          }}
        >
          {error}
        </FormHelperText>
      )}
    </Box>
  );

  const formShellSx = {
    width: responsiveWidth,
    mx: isAccountLayout ? 'auto' : isMobile ? 'auto' : undefined,
    maxWidth: '100%',
    transition: 'none',
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      paddingTop: '16px',
      paddingRight: '12px',
      paddingBottom: '16px',
      paddingLeft: '12px',
      gap: '8px',
      borderWidth: '1px',
      transition: 'none',
      '& fieldset': {
        borderRadius: '12px',
        borderWidth: '1px',
        borderColor: '#A2ACB1',
      },
      ...borderOverrides,
    },
  };

  return question?.tooltip?.message ? (
    <Tooltip
      title={<span>{question.tooltip.message}</span>}
      placement="right"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            letterSpacing: '-0.002em',
            borderRadius: '12px',
            border: '2px solid #E3E800',
            backgroundColor: '#FFFFFF',
            padding: '16px',
            boxShadow: '0px 3px 8px #162F3B1F, 0px 1px 2px #A2ACB133',
            maxWidth: '297px',
            color: '#000',
            '& .MuiTooltip-arrow': { color: '#E3E800' },
          },
        },
        arrow: { sx: { color: '#E3E800' } },
      }}
    >
      <FormControl
        fullWidth
        error={!!(revealError && error)}
        variant="outlined"
        sx={formShellSx}
      >
        {content}
      </FormControl>
    </Tooltip>
  ) : (
    <FormControl
      fullWidth
      error={!!(revealError && error)}
      variant="outlined"
      sx={formShellSx}
    >
      {content}
    </FormControl>
  );
};

export default SearchTagSelectComponent;
